import { HStack, Icon } from "@chakra-ui/react";
import { useCallback, useState, useEffect, useMemo } from "react";
import { MdErrorOutline } from "react-icons/md";
import WeekNavigator from "features/MeetingEvents/Components/WeekNavigator";
import { useUserListQuery } from "features/User/hooks";
import TableFilterSelect from "components/Select/TableFilterSelect";
import FilterButton from "components/Button/FilterButton";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import { COMMS_STATUSES, STAGE_OPTIONS } from "features/Resources/Shared/constants";
import { TabOption, TabSelector } from "components/TabSelector";

export type ViewMode = "week" | "all";
export type SortMode = "effective_deliver_at_asc" | "effective_deliver_at_desc";

export interface EmailsFilters {
  q?: string;
  recipient_id?: string;
  recipient_type?: string;
  start_date?: string;
  end_date?: string;
  view_mode?: ViewMode;
  has_issues?: string;
  status?: string;
  stage_key?: string;
  sort?: SortMode;
}

const DEFAULT_EMAIL_SORT = "effective_deliver_at_desc";
const DEFAULT_EMAIL_SORT_OPTIONS: SortOption[] = [
  { label: "Deliver At: Oldest First", value: "effective_deliver_at_asc" },
  { label: "Deliver At: Newest First", value: "effective_deliver_at_desc" },
];

interface EmailsFiltersProps {
  filters: EmailsFilters;
  onFiltersChange: (filters: EmailsFilters) => void;
  onReset: () => void;
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
  sortOptions?: SortOption[];
}

type FilterOption = { label: string; value: string };
type RecipientOption = FilterOption;
type StatusOption = FilterOption;
type StageOption = FilterOption;

function FilterTabs({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TabSelector value={value} onChange={onChange}>
      {options.map((option) => (
        <TabOption key={option.value} value={option.value}>
          {option.label}
        </TabOption>
      ))}
    </TabSelector>
  );
}

const viewModeOptions = [
  { label: "View by Week", value: "week" },
  { label: "View All", value: "all" },
];

const statusOptions = Array.from(COMMS_STATUSES.values()).map((status) => ({
  label: status.label,
  value: status.value,
}));

export default function EmailsFiltersComponent({
  filters,
  onFiltersChange,
  onReset,
  currentSort = DEFAULT_EMAIL_SORT,
  defaultSortString = DEFAULT_EMAIL_SORT,
  onSortChange: onSortChangeProp,
  sortOptions = DEFAULT_EMAIL_SORT_OPTIONS,
}: EmailsFiltersProps) {
  const [localSort, setLocalSort] = useState(DEFAULT_EMAIL_SORT);
  const onSortChange = onSortChangeProp || setLocalSort;
  const effectiveSort = onSortChangeProp ? currentSort : localSort;
  const { data: allUsers = [] } = useUserListQuery();
  const [selectedRecipient, setSelectedRecipient] = useState<RecipientOption | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<StatusOption | null>(null);
  const [selectedStage, setSelectedStage] = useState<StageOption | null>(null);

  const allRecipientOptions = useMemo(
    () =>
      allUsers.map((user) => ({
        label: user.fullName,
        value: user.id,
      })),
    [allUsers],
  );

  // Sync selectedRecipient when filters.recipient_id changes
  useEffect(() => {
    if (filters.recipient_id && !selectedRecipient && allRecipientOptions.length > 0) {
      const matchingRecipient = allRecipientOptions.find((option) => option.value === filters.recipient_id);
      if (matchingRecipient) {
        setSelectedRecipient(matchingRecipient);
      }
    } else if (!filters.recipient_id && selectedRecipient) {
      setSelectedRecipient(null);
    }
  }, [filters.recipient_id, allRecipientOptions, selectedRecipient]);

  // Sync selectedStatus when filters.status changes
  useEffect(() => {
    if (filters.status && !selectedStatus) {
      const matchingStatus = statusOptions.find((option) => option.value === filters.status);
      if (matchingStatus) {
        setSelectedStatus(matchingStatus);
      }
    } else if (!filters.status && selectedStatus) {
      setSelectedStatus(null);
    }
  }, [filters.status, selectedStatus]);

  // Sync selectedStage when filters.stage_key changes
  useEffect(() => {
    if (filters.stage_key && !selectedStage) {
      const matchingStage = STAGE_OPTIONS.find((option) => option.value === filters.stage_key);
      if (matchingStage) {
        setSelectedStage(matchingStage);
      }
    } else if (!filters.stage_key && selectedStage) {
      setSelectedStage(null);
    }
  }, [filters.stage_key, selectedStage]);

  const handleFilterChange = useCallback(
    (key: keyof EmailsFilters, value: string | undefined) => {
      const newValue = value === "" ? undefined : value;
      if (filters[key] !== newValue) {
        const updatedFilters = {
          ...filters,
          [key]: newValue,
        };

        // When setting recipient_id, also set recipient_type
        if (key === "recipient_id") {
          updatedFilters.recipient_type = newValue ? "User" : undefined;
        }

        onFiltersChange(updatedFilters);
      }
    },
    [filters, onFiltersChange],
  );

  const loadRecipientOptions = async (inputValue: string): Promise<RecipientOption[]> => {
    if (!inputValue || inputValue.length < 2) {
      return allRecipientOptions;
    }

    const filtered = allRecipientOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
    return filtered;
  };

  const handleWeekChange = useCallback(
    (startDate: string, endDate: string) => {
      onFiltersChange({
        ...filters,
        start_date: startDate,
        end_date: endDate,
      });
    },
    [filters, onFiltersChange],
  );

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) =>
      key !== "start_date" && key !== "end_date" && key !== "view_mode" && value !== undefined && value !== "",
  );

  const isWithIssuesActive = filters.has_issues === "true";

  const handleWithIssuesToggle = useCallback(() => {
    if (isWithIssuesActive) {
      handleFilterChange("has_issues", undefined);
    } else {
      handleFilterChange("has_issues", "true");
    }
  }, [isWithIssuesActive, handleFilterChange]);

  const handleResetFilters = useCallback(() => {
    setSelectedRecipient(null);
    setSelectedStatus(null);
    setSelectedStage(null);
    onReset();
  }, [onReset]);

  // Build active filters for display
  const activeFilters: ActiveFilter[] = [];

  if (filters.recipient_id) {
    activeFilters.push({
      key: "recipient",
      label: `Recipient: ${selectedRecipient?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedRecipient(null);
        handleFilterChange("recipient_id", undefined);
      },
    });
  }

  if (filters.status) {
    activeFilters.push({
      key: "status",
      label: `Status: ${selectedStatus?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedStatus(null);
        handleFilterChange("status", undefined);
      },
    });
  }

  if (filters.stage_key) {
    activeFilters.push({
      key: "stage",
      label: `Stage: ${selectedStage?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedStage(null);
        handleFilterChange("stage_key", undefined);
      },
    });
  }

  if (filters.has_issues) {
    activeFilters.push({
      key: "has_issues",
      label: "With Issues",
      onRemove: () => handleFilterChange("has_issues", undefined),
    });
  }

  return (
    <>
      <HStack justifyContent="space-between" flexWrap="wrap">
        <FilterTabs
          options={viewModeOptions}
          value={filters.view_mode || "week"}
          onChange={(value) => handleFilterChange("view_mode", value as ViewMode)}
        />

        {filters.view_mode === "week" && (
          <WeekNavigator startTime={filters.start_date} endTime={filters.end_date} onWeekChange={handleWeekChange} />
        )}
      </HStack>
      <FilterSortSearchBar
        searchPlaceholder="Search by email name or recipient..."
        searchValue={filters.q}
        onSearchChange={(value) => handleFilterChange("q", value)}
        currentSort={effectiveSort}
        defaultSort={defaultSortString}
        onSortChange={onSortChange}
        sortOptions={sortOptions}
        quickFilters={
          <FilterButton
            onClick={handleWithIssuesToggle}
            isActive={isWithIssuesActive}
            icon={<Icon as={MdErrorOutline} color="statusRed.text" boxSize={4} />}
            size="sm"
          >
            With Issues
          </FilterButton>
        }
        allFilters={
          <>
            <FilterButton
              onClick={handleWithIssuesToggle}
              isActive={isWithIssuesActive}
              icon={<Icon as={MdErrorOutline} boxSize={4} />}
            >
              With Issues
            </FilterButton>
            <TableFilterSelect
              placeholder="Filter by status"
              value={selectedStatus}
              onChange={(option) => {
                setSelectedStatus(option);
                handleFilterChange("status", option?.value);
              }}
              defaultOptions={statusOptions}
              minWidth="160px"
              isClearable={false}
            />
            <TableFilterSelect
              placeholder="Filter by recipient"
              value={selectedRecipient}
              onChange={(option) => {
                setSelectedRecipient(option);
                handleFilterChange("recipient_id", option?.value);
              }}
              loadOptions={loadRecipientOptions}
              defaultOptions={allRecipientOptions}
              isClearable={false}
            />
            <TableFilterSelect
              placeholder="Filter by stage"
              value={selectedStage}
              onChange={(option) => {
                setSelectedStage(option);
                handleFilterChange("stage_key", option?.value);
              }}
              defaultOptions={STAGE_OPTIONS}
              minWidth="160px"
              isClearable={false}
            />
          </>
        }
        activeFilters={activeFilters}
        onReset={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
      />
    </>
  );
}
